import React from 'react';
import clsx from 'clsx';
import { FormattedMessage } from 'react-intl';
import { ProgressCircular } from '@americanexpress/dls-react';
import { ReportInlineModuleError } from '@americanexpress/travel-error-base';
import { useConditionalFeature } from '@americanexpress/conditional-features';
import { useSelector } from 'react-redux';
import { getAutomationCardOffersClassName } from '../../selectors/getHomeSelectors';
import { getCardOffersContentFromPersona } from '../../utils/personaUtils';
import { getCardType } from '../../utils/travelerDataUtils';
import { hasHomePageCardOffersRule, hasMultipleRewardsRule } from '../../constants/featureRulesConstants';
import { MODULE_NAME } from '../../constants/modules';
import { useHomepageCardOffers } from '../../hooks/useHomepageCardOffers';
import { usePersonaApi } from '../../hooks/usePersonaApi';
import { useTravelerData } from '../../hooks/useTravelerData';
import CardOffers from './CardOffers';
import ErrorMessage from '../ErrorMessage/ErrorMessage';
import getIsWaitingForMultipleRewardsSelector from '../../selectors/getIsWaitingForMultipleRewardsSelector';
import { PersonalizedContentError } from '../PersonalizedContent/PersonalizedContentError';
import style from './CardOffersContainer.scss';
import { hasNonEmptyEntries } from '../../utils/hasNonEmptyEntries.ts';

const CardOffersContainer = () => {
  const isInMrMarket = useConditionalFeature(hasMultipleRewardsRule);
  const isWaitingForMultipleRewards = useSelector(getIsWaitingForMultipleRewardsSelector);
  const hasHomePageCardOffers = useConditionalFeature(hasHomePageCardOffersRule);
  const automationCardOffersId = useSelector(
    getAutomationCardOffersClassName(hasHomePageCardOffers)
  );
  const { travelerData, travelerError } = useTravelerData();
  const travelerAccounts = travelerData?.loyalty?.accounts || [];
  const hasMultipleAccounts = travelerAccounts.length > 1;

  const {
    data: personaData,
    isLoading: personaLoading,
    error: personaError,
  } = usePersonaApi();

  const {
    error: homepageCardOffersError,
    data: homepageCardOffersData,
    isLoading: homepageCardOffersLoading,
  } = useHomepageCardOffers();

  const displayLoadingState = personaLoading
    || (!travelerError && isInMrMarket && hasMultipleAccounts && isWaitingForMultipleRewards)
    || homepageCardOffersLoading;

  if (displayLoadingState) {
    return (
      <div className={clsx('text-align-center pad flex', style.loaderContainer)}>
        <ProgressCircular size="lg" className="margin-auto" />
      </div>
    );
  }

  let cardOffersData;
  let hasCardOfferError;
  const cardType = getCardType(travelerData);

  if (hasHomePageCardOffers && homepageCardOffersData) {
    cardOffersData = homepageCardOffersData.body;
    hasCardOfferError = homepageCardOffersError;
  } else {
    cardOffersData = getCardOffersContentFromPersona(
      personaData
    );
    hasCardOfferError = personaError;
  }

  const hasInsufficientCardOffers = !hasNonEmptyEntries(cardOffersData);

  if (hasCardOfferError || hasInsufficientCardOffers) {
    return (
      <div className="pad-responsive-extra-tb" data-testid={`${MODULE_NAME}-card-offer-error`}>
        <section className="container">
          <ReportInlineModuleError
            moduleID={MODULE_NAME}
            otherData={{
              component: 'CardOffersContainer',
              causes: {
                hasCardOfferError: !!hasCardOfferError,
                hasInsufficientCardOffers,
              },
            }}
          >
            <ErrorMessage
              message={
                <FormattedMessage id="error.unableToLoadContentMessage" />
              }
            />
          </ReportInlineModuleError>
        </section>
      </div>
    );
  }

  return (
    <div
      data-testid={`${MODULE_NAME}-homepage-card-offers-container`}
      className="pad-3-b"
    >
      <section
        id={`${MODULE_NAME}-homepage-card-offers`}
        data-testid={automationCardOffersId}
        className={clsx(
          'container pad-0-lr-md-down',
          automationCardOffersId
        )}
      >
        <PersonalizedContentError
          hasTravelerError={travelerError}
        />
        <CardOffers
          cardOffersData={cardOffersData}
          cardType={cardType}
          hasTravelerError={travelerError}
          travelerData={travelerData}
        />
      </section>
    </div>
  );
};

export default CardOffersContainer;
